import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type CustomField = Schemas['CustomFieldDto'];
export type CustomFieldRequest = Schemas['CustomFieldRequest'];
export type ProjectFieldSetting = Schemas['ProjectFieldSettingDto'];
export type CustomFieldEntity = CustomField['entity'];
export type CustomFieldValue = string | number | boolean | string[] | null;
export type CustomFieldValues = Record<string, CustomFieldValue>;

const key = (ws: string) => ['workspace', ws, 'custom-fields'] as const;
const wsPath = (ws: string) => ({ params: { path: { workspaceId: ws } } });

export function useCustomFields(ws: string, enabled = true) {
  return useQuery({
    queryKey: key(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/custom-fields', wsPath(ws))),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveCustomField(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CustomFieldRequest & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/custom-fields/{id}', { params: { path: { workspaceId: ws, id } }, body }))
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/custom-fields', { ...wsPath(ws), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useDeleteCustomField(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/custom-fields/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useProjectFieldSettings(ws: string, projectId: string) {
  return useQuery({
    queryKey: [...key(ws), 'project', projectId],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/custom-fields', { params: { path: { workspaceId: ws, projectId } } })),
  });
}

export function useSetProjectFieldSetting(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['ProjectFieldSettingRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/projects/{projectId}/custom-fields', { params: { path: { workspaceId: ws, projectId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...key(ws), 'project', projectId] }),
  });
}

/** Für ein Projekt wirksame Zeiteintrags-Felder (alle aktiven Felder, Projekt-Overrides beachtet). */
export function useEntryFields(ws: string, projectId: string | null | undefined, enabled = true) {
  const fields = useCustomFields(ws, enabled);
  const settings = useQuery({
    queryKey: [...key(ws), 'project', projectId ?? 'none'],
    queryFn: () =>
      projectId
        ? unwrap(api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/custom-fields', { params: { path: { workspaceId: ws, projectId } } }))
        : Promise.resolve([] as ProjectFieldSetting[]),
    enabled: enabled && !!fields.data?.some((f) => f.entity === 'TIME_ENTRY'),
  });
  const list = (fields.data ?? [])
    .filter((f) => f.entity === 'TIME_ENTRY' && !f.archived)
    .map((f) => {
      const override = settings.data?.find((s) => s.fieldId === f.id);
      return {
        field: f,
        enabled: override ? override.enabled : f.allProjects,
        defaultValue: (override?.defaultValue ?? f.defaultValue ?? null) as CustomFieldValue,
      };
    })
    .filter((f) => f.enabled);
  return list;
}

export function useUserFieldValues(ws: string, userId: string | undefined) {
  return useQuery({
    queryKey: [...key(ws), 'user', userId],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/users/{userId}/profile-fields', { params: { path: { workspaceId: ws, userId: userId! } } })),
    enabled: !!userId,
  });
}

export function useSetUserFieldValues(ws: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      unwrap(
        api.PUT('/api/v1/workspaces/{workspaceId}/users/{userId}/profile-fields', {
          params: { path: { workspaceId: ws, userId } },
          body: { values: values as Schemas['UserFieldValuesRequest']['values'] },
        }),
      ),
    onSuccess: (data) => qc.setQueryData([...key(ws), 'user', userId], data),
  });
}

/** JSON der Feldwerte eines Eintrags lesen. */
export function parseValues(json: string | null | undefined): CustomFieldValues {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as CustomFieldValues) : {};
  } catch {
    return {};
  }
}

/** Anzeige eines Feldwerts. */
export function formatValue(value: unknown, yes = '✓', no = '–'): string {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? yes : no;
  return String(value);
}
